import type { JSX } from "solid-js";
import {
	type Accessor,
	createEffect,
	createMemo,
	createSignal,
} from "solid-js";

type MaybeAccessor<T> = T | Accessor<T>;

type UseAriaGridOptions = {
	columnCount: MaybeAccessor<number>;
	itemCount: MaybeAccessor<number>;
	selectedIndex?: Accessor<number>;
	onActivate?: (index: number) => void;
};

const resolveMaybeAccessor = <T>(value: MaybeAccessor<T>): T =>
	typeof value === "function" ? (value as Accessor<T>)() : value;

const clamp = (value: number, min: number, max: number) =>
	Math.min(Math.max(value, min), max);

/**
 * Implements the WAI-ARIA grid keyboard pattern for small interactive grids.
 *
 * The caller owns the markup and should render a container with `role="grid"`,
 * row wrappers using `rowProps`, and focusable cells using `cellProps`.
 * Focus is roved between cells with Arrow keys, Home/End move within a row,
 * Ctrl+Home/Ctrl+End jump to the first/last cell, and Enter/Space activate the
 * focused cell through `onActivate`.
 */
export const useAriaGrid = (options: UseAriaGridOptions) => {
	const [focusedIndex, setFocusedIndex] = createSignal(0);
	const cells = new Map<number, HTMLElement>();

	const columnCount = createMemo(() =>
		Math.max(1, resolveMaybeAccessor(options.columnCount)),
	);
	const itemCount = createMemo(() =>
		Math.max(0, resolveMaybeAccessor(options.itemCount)),
	);
	const rowCount = createMemo(() => Math.ceil(itemCount() / columnCount()));

	const focusCell = (index: number) => {
		const nextIndex = clamp(index, 0, Math.max(0, itemCount() - 1));
		setFocusedIndex(nextIndex);
		queueMicrotask(() => cells.get(nextIndex)?.focus());
	};

	createEffect(() => {
		const count = itemCount();
		if (count === 0) {
			setFocusedIndex(0);
			return;
		}

		const selectedIndex = options.selectedIndex?.();
		if (
			selectedIndex !== undefined &&
			selectedIndex >= 0 &&
			selectedIndex < count
		) {
			setFocusedIndex(selectedIndex);
			return;
		}

		setFocusedIndex((index) => clamp(index, 0, count - 1));
	});

	const currentRowStart = () =>
		Math.floor(focusedIndex() / columnCount()) * columnCount();

	const currentRowEnd = () =>
		Math.min(currentRowStart() + columnCount() - 1, itemCount() - 1);

	const handleKeyDown: JSX.EventHandlerUnion<HTMLDivElement, KeyboardEvent> = (
		event,
	) => {
		if (itemCount() === 0) {
			return;
		}

		let nextIndex: number | undefined;

		switch (event.key) {
			case "ArrowRight":
				nextIndex = focusedIndex() + 1;
				break;
			case "ArrowLeft":
				nextIndex = focusedIndex() - 1;
				break;
			case "ArrowDown":
				nextIndex = focusedIndex() + columnCount();
				break;
			case "ArrowUp":
				nextIndex = focusedIndex() - columnCount();
				break;
			case "Home":
				nextIndex = event.ctrlKey ? 0 : currentRowStart();
				break;
			case "End":
				nextIndex = event.ctrlKey ? itemCount() - 1 : currentRowEnd();
				break;
			case "Enter":
			case " ":
				event.preventDefault();
				options.onActivate?.(focusedIndex());
				return;
			default:
				return;
		}

		event.preventDefault();
		focusCell(nextIndex);
	};

	return {
		focusedIndex,
		gridProps: () => ({
			"aria-colcount": columnCount(),
			"aria-rowcount": rowCount(),
			onKeyDown: handleKeyDown,
		}),
		rowProps: (rowIndex: number) => ({
			role: "row" as const,
			"aria-rowindex": rowIndex + 1,
		}),
		cellProps: (index: number) => ({
			role: "gridcell" as const,
			tabIndex: focusedIndex() === index ? 0 : -1,
			"aria-colindex": (index % columnCount()) + 1,
			"aria-selected": options.selectedIndex?.() === index,
			ref: (el: HTMLElement) => {
				cells.set(index, el);
			},
			onFocus: () => {
				setFocusedIndex(index);
			},
		}),
	};
};
